'use client';
import React, { useState } from 'react';
import { Bookmark, ShoppingBag, MapPin, Briefcase, ChevronRight, Search, Eye, Download, MessageSquare, CheckCircle, Shield, Clock } from 'lucide-react';
import Link from 'next/link';

interface Candidate {
  id: string;
  firstName: string;
  province: string;
  jobType: string;
  workReadinessScore: number;
  verified: boolean;
  savedAt: string;
  purchased: boolean;
  purchasedAt?: string;
  skills: string[];
  availability: string;
  notes?: string;
}

const mockCandidates: Candidate[] = [
  {
    id: 'cand-001',
    firstName: 'Thandi',
    province: 'Gauteng',
    jobType: 'Administration',
    workReadinessScore: 87,
    verified: true,
    savedAt: '10 Aug 2026',
    purchased: true,
    purchasedAt: '12 Aug 2026',
    skills: ['MS Office', 'Data Entry', 'Customer Service'],
    availability: 'Immediately',
    notes: 'Strong communicator, excellent references.',
  },
  {
    id: 'cand-002',
    firstName: 'Sipho',
    province: 'KwaZulu-Natal',
    jobType: 'General Labour',
    workReadinessScore: 79,
    verified: true,
    savedAt: '14 Aug 2026',
    purchased: true,
    purchasedAt: '15 Aug 2026',
    skills: ['Forklift', 'Warehouse', 'Stock Control'],
    availability: '1 week notice',
  },
  {
    id: 'cand-003',
    firstName: 'Lerato',
    province: 'Gauteng',
    jobType: 'Retail & Sales',
    workReadinessScore: 82,
    verified: true,
    savedAt: '18 Aug 2026',
    purchased: false,
    skills: ['POS Systems', 'Merchandising', 'Customer Relations'],
    availability: 'Immediately',
  },
  {
    id: 'cand-004',
    firstName: 'Nomvula',
    province: 'Western Cape',
    jobType: 'Hospitality',
    workReadinessScore: 91,
    verified: true,
    savedAt: '20 Aug 2026',
    purchased: false,
    skills: ['Food & Beverage', 'Front of House', 'Barista'],
    availability: '2 weeks notice',
  },
];

type TabKey = 'all' | 'purchased' | 'saved';

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'all', label: 'All', icon: <Bookmark size={13} /> },
  { key: 'purchased', label: 'Purchased', icon: <ShoppingBag size={13} /> },
  { key: 'saved', label: 'Saved', icon: <Bookmark size={13} /> },
];

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 85 ? 'var(--charcoal)' : score >= 70 ? '#92400e' : '#6b7280';
  const bg = score >= 85 ? 'var(--amber)' : score >= 70 ? '#fef3c7' : 'var(--muted)';
  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: bg, color }}>
      {score}% WR
    </span>
  );
}

export default function EmployerSavedCandidates() {
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [noteTarget, setNoteTarget] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [candidates, setCandidates] = useState(mockCandidates);

  const filtered = candidates.filter((c) => {
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'purchased' && c.purchased) ||
      (activeTab === 'saved' && !c.purchased);
    const matchesSearch =
      !searchQuery ||
      c.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.province.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.jobType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.skills.some((s) => s.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const handleSaveNote = (id: string) => {
    setCandidates((prev) =>
      prev.map((c) => (c.id === id ? { ...c, notes: noteText } : c))
    );
    setNoteTarget(null);
    setNoteText('');
  };

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold" style={{ color: 'var(--navy)' }}>Saved Candidates</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {candidates.filter((c) => c.purchased).length} purchased · {candidates.filter((c) => !c.purchased).length} saved
            </p>
          </div>
          <Link
            href="/directory"
            className="text-xs font-bold flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors hover:bg-muted"
            style={{ color: 'var(--navy)' }}
          >
            Browse Directory <ChevronRight size={12} />
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            type="text"
            placeholder="Search by name, province, skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-9 text-xs py-2.5"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-border -mx-5 px-5 overflow-x-auto">
          {tabs.map((tab) => {
            const count =
              tab.key === 'all'
                ? candidates.length
                : tab.key === 'purchased'
                ? candidates.filter((c) => c.purchased).length
                : candidates.filter((c) => !c.purchased).length;
            return (
              <button
                key={`emp-cand-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className="px-3 py-2.5 text-xs font-semibold whitespace-nowrap transition-all duration-150 border-b-2 flex items-center gap-1.5"
                style={{
                  borderColor: activeTab === tab.key ? 'var(--primary)' : 'transparent',
                  color: activeTab === tab.key ? 'var(--navy)' : 'var(--muted-foreground)',
                }}
              >
                {tab.icon}
                {tab.label}
                {count > 0 && (
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full font-bold tabular-nums"
                    style={{
                      background: activeTab === tab.key ? 'var(--primary)' : 'var(--border)',
                      color: activeTab === tab.key ? 'white' : 'var(--muted-foreground)',
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Candidate list */}
      <div className="divide-y divide-border">
        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <Bookmark size={28} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--navy)' }}>No candidates here</p>
            <p className="text-xs text-muted-foreground">
              {searchQuery ? 'Try a different search term.' : 'Browse the directory to save or purchase candidate profiles.'}
            </p>
          </div>
        ) : (
          filtered.map((candidate) => (
            <div key={candidate.id} className="p-5 hover:bg-muted/20 transition-colors">
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
                  style={{ background: 'var(--off-white)', color: 'var(--charcoal)' }}
                >
                  {candidate.firstName[0]}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-bold text-sm" style={{ color: 'var(--navy)' }}>
                      {candidate.firstName} {candidate.purchased ? '' : '—'}
                    </span>
                    {candidate.verified && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--off-white)', color: 'var(--charcoal)' }}>
                        <Shield size={10} />Verified
                      </span>
                    )}
                    <ScoreBadge score={candidate.workReadinessScore} />
                    {candidate.purchased ? (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#dcfce7', color: '#166534' }}>
                        <CheckCircle size={10} />Purchased
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}>
                        <Bookmark size={10} />Saved
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    <span className="flex items-center gap-1"><MapPin size={10} />{candidate.province}</span>
                    <span className="flex items-center gap-1"><Briefcase size={10} />{candidate.jobType}</span>
                    <span className="flex items-center gap-1"><Clock size={10} />{candidate.availability}</span>
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {candidate.skills.map((skill) => (
                      <span key={skill} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                        {skill}
                      </span>
                    ))}
                  </div>

                  {/* Notes */}
                  {candidate.notes && noteTarget !== candidate.id && (
                    <div className="rounded-lg px-3 py-2 mb-3 text-xs" style={{ background: 'var(--off-white)', color: 'var(--muted-foreground)' }}>
                      <span className="font-semibold" style={{ color: 'var(--charcoal)' }}>Your note: </span>
                      {candidate.notes}
                    </div>
                  )}

                  {/* Note editor */}
                  {noteTarget === candidate.id && (
                    <div className="mb-3">
                      <textarea
                        className="input-field text-xs resize-none"
                        rows={2}
                        placeholder="Add a private note about this candidate..."
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-1.5">
                        <button
                          onClick={() => handleSaveNote(candidate.id)}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg"
                          style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}
                        >
                          Save Note
                        </button>
                        <button
                          onClick={() => { setNoteTarget(null); setNoteText(''); }}
                          className="text-xs px-3 py-1.5 rounded-lg"
                          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Link
                      href={`/candidate-profile?id=${candidate.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                      style={{ background: 'var(--off-white)', color: 'var(--charcoal)' }}
                    >
                      <Eye size={12} />View Profile
                    </Link>

                    {candidate.purchased && (
                      <button
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                        style={{ background: 'var(--off-white)', color: 'var(--charcoal)' }}
                      >
                        <Download size={12} />Download CV
                      </button>
                    )}

                    <button
                      onClick={() => { setNoteTarget(candidate.id); setNoteText(candidate.notes || ''); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                      style={{ background: 'var(--off-white)', color: 'var(--charcoal)' }}
                    >
                      <MessageSquare size={12} />{candidate.notes ? 'Edit Note' : 'Add Note'}
                    </button>

                    {!candidate.purchased && (
                      <button
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                        style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}
                      >
                        <ShoppingBag size={12} />Unlock — R1,000
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
